import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { FaHistory, FaRegCalendarAlt, FaRegFutbol } from "react-icons/fa";
import SimpleBox from "../../components/layout/container/SimpleBox";
import PreviousNextButton from "../../components/input/button/PreviousNextButton";
import ShadowBox from "../layout/container/ShadowBox";
import Game from "../dataitem/Game";
import dayjs from "dayjs";

// `direction` doubles as which icon/accent to show: -1 (past results) gets a
// history icon, +1 (upcoming) gets a calendar icon — keeps the two cards
// (otherwise identical in shape) visually distinguishable at a glance.
const GameSet = ({ games, direction, title = null }) => {

    const theme = useTheme();
    const Icon = direction < 0 ? FaHistory : FaRegCalendarAlt;

    const gamesSorted = [...games].sort((a, b) => b.date < a.date ? 1 : b.date > a.date ? -1 : 0);

    // `games` starts empty and is filled in once the season's data loads —
    // this has to reset whenever that happens (not just compute an initial
    // value once), otherwise the index picked for an empty list (-1 for the
    // "latest" card) would stick around forever after real games arrive.
    const [currentIndex, setCurrentIndex] = useState(direction > 0 ? 0 : gamesSorted.length - 1);
    useEffect(() => {
        setCurrentIndex(direction > 0 ? 0 : gamesSorted.length - 1);
    }, [games]); // eslint-disable-line

    const header = Boolean(title) ? (
        <SimpleBox gap={2} mb={theme.spacing(4)} justifyContent="flex-start">
            <Icon color={theme.palette.primary.dark} size={18} />
            <Typography variant="h3">{title}</Typography>
        </SimpleBox>
    ) : null;

    if (games.length === 0) {
        return (
            <ShadowBox width="100%" backgroundColor="white.dark" display="flex">
                <SimpleBox fullWidth={true} alignItems="left" flexDirection="column">
                    {header}
                    <SimpleBox fullWidth={true} justifyContent="center" flexDirection="column" gap={2} height="100%" py={theme.spacing(6)}>
                        <FaRegFutbol color={theme.palette.davysGray.light} size={28} />
                        <Typography color={theme.palette.davysGray.main}>
                            {direction < 0 ? "Ainda não há jogos realizados" : "Ainda não há jogos agendados"}
                        </Typography>
                    </SimpleBox>
                </SimpleBox>
            </ShadowBox>
        )
    }

    const actionPrev = currentIndex > 0 ? () => setCurrentIndex(currentIndex - 1) : null;
    const actionNext = currentIndex < gamesSorted.length - 1 ? () => setCurrentIndex(currentIndex + 1) : null;
    const game = currentIndex >= 0 && currentIndex < gamesSorted.length ? gamesSorted[currentIndex] : null;

    return (
        <ShadowBox width="100%" backgroundColor="white.dark" display="flex" borderRadius="12px">
            {header}
            <SimpleBox mb={4}>
                <PreviousNextButton action={actionPrev} type="previous" />
                {
                    Boolean(game) ? (
                        <Box
                            sx={{
                                px: theme.spacing(3),
                                py: theme.spacing(1),
                                borderRadius: "999px",
                                backgroundColor: theme.palette.secondary.light,
                            }}
                        >
                            <Typography fontWeight="bold" fontSize="0.9rem">{dayjs(game.date).format("DD MMM YYYY").toUpperCase()}</Typography>
                        </Box>
                    ) : null
                }
                <PreviousNextButton action={actionNext} type="next" />
            </SimpleBox>
            <SimpleBox flexDirection="column" justifyContent="center">
                {Boolean(game) ? <Game game={game} forceMobile={true} /> : null}
            </SimpleBox>
        </ShadowBox>
    )
}

export default GameSet;
